import React from 'react'
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom'
import { Backdrop, CircularProgress, CssBaseline } from '@mui/material'
import { Snackbar } from './component/Snackbar'
import { useDispatch, useSelector } from './store'
import { LocalStorage } from './enum/storage'
import { getLoading } from './store/common/selector'
import { refreshToken } from './store/auth/actionCreator'
import { initNotification } from './store/support/actionCreator'
import { fetchProfile } from './store/settings/actionCreator'
import { useAuthEffect } from './hook/useAuthEffect'
import { LoginPage } from './page/AuthLoginPage'
import { RegisterPage } from './page/AuthRegisterPage'
import { RegisterSentPage } from './page/AuthRegisterSentPage'
import { TwoStepPage } from './page/Auth2StepPage'
import { ForgotPage } from './page/AuthForgotPage'
import { ForgotSentPage } from './page/AuthForgotSentPage'
import { ChangePasswordPage } from './page/AuthChangePassword'
import { DashboardPage } from './page/DashboardPage'
import { PaymentPage } from './page/PaymentPage'
import { MarketingPage } from './page/MarketingPage'
import { MarketingPageV2 } from './page/MarketingPageV2'
import { QrRegistrationPageV2 } from './page/QrRegistrationPageV2'
import { AdminsPageV2 } from './page/AdminsPageV2'
import { EnterprisePageV2 } from './page/EnterprisePageV2'
import { ApikeyPageV2 } from './page/ApikeyPageV2'
import { SettingsPageV2 } from './page/SettingsPageV2'
import { SupportPageV2 } from './page/SupportPageV2'
import { SystemPageV2 } from './page/SystemPageV2'
import { PaymentPageV2 } from './page/PaymentPageV2'
import { MessagesPageV2 } from './page/MessagesPageV2'
import { PackagePage } from './page/PackagePage'
import { PackagePageV2 } from './page/PackagePageV2'
import { AdminsPage } from './page/AdminsPage'
import { SubscribersPage } from './page/SubscribersPage'
import { SubscribersPageV2 } from './page/SubscribersPageV2'
import { EnterprisePage } from './page/EnterprisePage'
import { QrRegistrationPage } from './page/QrRegistrationPage'
import { ApikeyPage } from './page/ApikeyPage'
import { SupportPage } from './page/SupportPage'
import { SettingsPage } from './page/SettingsPage'
import { SubscriberPage } from './page/SubscriberPage'
import { SubscriberPageV2 } from './page/SubscriberPageV2'
import { ResultPageV2 } from './page/ResultPageV2'
import { DashboardPageV2 } from './page/DashboardPageV2'
import { ResultPage } from './page/ResultPage'
import { SystemPage } from './page/SystemPage'

type PrivateRouteProps = {
  children: JSX.Element
}

function PrivateRoute({ children }: PrivateRouteProps) {
  const dispatch = useDispatch()

  const token = localStorage.getItem(LocalStorage.TOKEN)

  const handleTokenOnVisibilityChange = React.useCallback(() => {
    if (!document.hidden) {
      dispatch(refreshToken())
    }
  }, [dispatch])

  React.useEffect(() => {
    const token = localStorage.getItem(LocalStorage.TOKEN)

    if (token === null) return

    dispatch(refreshToken())

    document.addEventListener('visibilitychange', handleTokenOnVisibilityChange)

    return function cleanup() {
      document.removeEventListener(
        'visibilitychange',
        handleTokenOnVisibilityChange,
      )
    }
  }, [dispatch, handleTokenOnVisibilityChange])

  useAuthEffect(() => {
    dispatch(initNotification)
    dispatch(fetchProfile)
  })

  return token ? children : <Navigate to="/auth" />
}

export function App() {
  const loading = useSelector(getLoading)

  return (
    <>
      <CssBaseline enableColorScheme />

      <div id="recaptcha" />

      <Backdrop open={loading} style={{ zIndex: 9999 }}>
        <CircularProgress color="primary" style={{ zIndex: 99999 }} />
      </Backdrop>

      <Snackbar />

      <BrowserRouter>
        <Routes>
          <Route path="/auth" element={<LoginPage />} />
          <Route path="/auth/register" element={<RegisterPage />} />
          <Route path="/auth/register/send" element={<RegisterSentPage />} />
          <Route path="/auth/forgot" element={<ForgotPage />} />
          <Route path="/auth/forgot/sent" element={<ForgotSentPage />} />
          <Route
            path="/auth/change/password"
            element={<ChangePasswordPage />}
          />
          <Route path="/auth/2step" element={<TwoStepPage />} />
          <Route
            path="/"
            element={
              <PrivateRoute>
                <DashboardPage />
              </PrivateRoute>
            }
          />
          <Route
            path="/payment_v2"
            element={
              <PrivateRoute>
                <PaymentPageV2 />
              </PrivateRoute>
            }
          />
          <Route
            path="/messages_v2"
            element={
              <PrivateRoute>
                <MessagesPageV2 />
              </PrivateRoute>
            }
          />
          <Route
            path="/payment"
            element={
              <PrivateRoute>
                <PaymentPage />
              </PrivateRoute>
            }
          />
          <Route
            path="/marketing"
            element={
              <PrivateRoute>
                <MarketingPage />
              </PrivateRoute>
            }
          />
          <Route
            path="/marketing_v2"
            element={
              <PrivateRoute>
                <MarketingPageV2 />
              </PrivateRoute>
            }
          />
          <Route
            path="/package"
            element={
              <PrivateRoute>
                <PackagePage />
              </PrivateRoute>
            }
          />
          <Route
            path="/package_v2"
            element={
              <PrivateRoute>
                <PackagePageV2 />
              </PrivateRoute>
            }
          />
          <Route
            path="/users/admins_v2"
            element={
              <PrivateRoute>
                <AdminsPageV2 />
              </PrivateRoute>
            }
          />
          <Route
            path="/users/admins"
            element={
              <PrivateRoute>
                <AdminsPage />
              </PrivateRoute>
            }
          />
          <Route
            path="/users/enterprise_v2"
            element={
              <PrivateRoute>
                <EnterprisePageV2 />
              </PrivateRoute>
            }
          />
          <Route
            path="/users/enterprise"
            element={
              <PrivateRoute>
                <EnterprisePage />
              </PrivateRoute>
            }
          />
          <Route
            path="/qr-registration_v2"
            element={
              <PrivateRoute>
                <QrRegistrationPageV2 />
              </PrivateRoute>
            }
          />
          <Route
            path="/qr-registration"
            element={
              <PrivateRoute>
                <QrRegistrationPage />
              </PrivateRoute>
            }
          />
          <Route
            path="/users/subscribers"
            element={
              <PrivateRoute>
                <SubscribersPage />
              </PrivateRoute>
            }
          />
          <Route
            path="/users/subscribers_v2"
            element={
              <PrivateRoute>
                <SubscribersPageV2 />
              </PrivateRoute>
            }
          />
          <Route
            path="/users/subscriber_v2/:id"
            element={
              <PrivateRoute>
                <SubscriberPageV2 />
              </PrivateRoute>
            }
          />
          <Route
            path="/users/subscriber/:id"
            element={
              <PrivateRoute>
                <SubscriberPage />
              </PrivateRoute>
            }
          />
          <Route
            path="/dashboard_v2"
            element={
              <PrivateRoute>
                <DashboardPageV2 />
              </PrivateRoute>
            }
          />
          <Route
            path="/result_v2/:id"
            element={
              <PrivateRoute>
                <ResultPageV2 />
              </PrivateRoute>
            }
          />
          <Route
            path="/result/:id"
            element={
              <PrivateRoute>
                <ResultPage />
              </PrivateRoute>
            }
          />
          <Route
            path="/apikey_v2"
            element={
              <PrivateRoute>
                <ApikeyPageV2 />
              </PrivateRoute>
            }
          />
          <Route
            path="/apikey"
            element={
              <PrivateRoute>
                <ApikeyPage />
              </PrivateRoute>
            }
          />
          <Route
            path="/system_v2"
            element={
              <PrivateRoute>
                <SystemPageV2 />
              </PrivateRoute>
            }
          />
          <Route
            path="/system"
            element={
              <PrivateRoute>
                <SystemPage />
              </PrivateRoute>
            }
          />
          <Route
            path="/support_v2"
            element={
              <PrivateRoute>
                <SupportPageV2 />
              </PrivateRoute>
            }
          />
          <Route
            path="/support"
            element={
              <PrivateRoute>
                <SupportPage />
              </PrivateRoute>
            }
          />
          <Route
            path="/settings_v2"
            element={
              <PrivateRoute>
                <SettingsPageV2 />
              </PrivateRoute>
            }
          />
          <Route
            path="/settings"
            element={
              <PrivateRoute>
                <SettingsPage />
              </PrivateRoute>
            }
          />
          <Route
            path="*"
            element={
              <PrivateRoute>
                <DashboardPage />
              </PrivateRoute>
            }
          />
        </Routes>
      </BrowserRouter>
    </>
  )
}
